import { FeetInchesInput } from "@/shared/components/FeetInchesInput";
import { GradeSelector } from "@/shared/selectors/GradeSelector";
import { HeatSelector } from "@/shared/selectors/HeatSelector";
import { SectionSelector } from "@/shared/selectors/SectionSelector";
import { backgroundColor } from "@/utils/theme";
import { CheckCircleOutline, Close, Delete, Edit } from "@mui/icons-material";
import {
	Box,
	IconButton,
	Stack,
	TableCell,
	TableRow,
	TextField,
	Tooltip,
} from "@mui/material";
import type { KeyboardEvent } from "react";
import { StackerBundle, StackerBundleDraft } from "../models/StackerBundle";
import { dimSx, formatLength, numCellSx } from "../utils/utils";
import { MILL_1 } from "@/setup/mill-config/mill-config";

const blockNonIntegerKeys = (e: KeyboardEvent<HTMLInputElement>) => {
	if (["e", "E", "+", "-", ".", ","].includes(e.key)) e.preventDefault();
};

export const DisplayPiecesRow = ({
	idx,
	row,
	disabled,
	onEdit,
	onDelete,
}: {
	idx: number;
	row: StackerBundle;
	disabled: boolean;
	onEdit: () => void;
	onDelete: () => void;
}) => {
	return (
		<TableRow
			hover
			sx={{
				"&:hover .row-actions": { opacity: 1 },
				"& td": { verticalAlign: "middle" },
				"&:last-child td, &:last-child th": { border: 0 },
			}}
		>
			<TableCell sx={dimSx}>{idx}</TableCell>
			<TableCell>{row.SectionName}</TableCell>
			<TableCell sx={row.HeatName ? undefined : dimSx}>
				{row.HeatName || "-"}
			</TableCell>
			<TableCell>{row.GradeName}</TableCell>
			<TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>
				{formatLength(row.LengthInches)}
			</TableCell>
			<TableCell sx={numCellSx}>
				{(row.GoodPieces ?? 0).toLocaleString()}
			</TableCell>
			<TableCell sx={{ ...numCellSx, ...(row.TotalWeight ? {} : dimSx) }}>
				{row.TotalWeight != null ? row.TotalWeight.toLocaleString() : "-"}
			</TableCell>
			<TableCell sx={{ ...numCellSx, ...(row.ScrapPieces ? {} : dimSx) }}>
				{row.ScrapPieces != null ? row.ScrapPieces.toLocaleString() : "-"}
			</TableCell>
			<TableCell sx={row.ScrapReason ? undefined : dimSx}>
				{row.ScrapReason || "-"}
			</TableCell>
			<TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
				<Box
					className="row-actions"
					sx={{ opacity: 0, transition: "opacity .1s" }}
				>
					<IconButton
						size="small"
						title="Edit row"
						disabled={disabled}
						onClick={onEdit}
					>
						<Edit fontSize="small" />
					</IconButton>
					<IconButton
						size="small"
						title="Delete row"
						color="error"
						disabled={disabled}
						onClick={onDelete}
					>
						<Delete fontSize="small" />
					</IconButton>
				</Box>
			</TableCell>
		</TableRow>
	);
};

export const EditPiecesRow = ({
	idx,
	draft,
	original,
	isMutating,
	onChange,
	onSave,
	onCancel,
}: {
	idx: number;
	draft: StackerBundleDraft;
	original?: StackerBundle;
	isMutating: boolean;
	onChange: (next: StackerBundleDraft) => void;
	onSave: () => void;
	onCancel: () => void;
}) => {
	const goodPieces = draft.GoodPieces ?? 0;

	const isNew = !original;
	const isDirty =
		isNew ||
		original.SectionId !== draft.SectionId ||
		original.HeatId !== draft.HeatId ||
		original.LengthInches !== draft.LengthInches ||
		(original.GoodPieces ?? 0) !== goodPieces ||
		(original.TotalWeight ?? null) !== (draft.TotalWeight ?? null) ||
		(original.ScrapPieces ?? null) !== (draft.ScrapPieces ?? null) ||
		original.ScrapReason !== draft.ScrapReason;

	const canSave =
		isDirty &&
		draft.SectionId > 0 &&
		draft.LengthInches > 0 &&
		goodPieces >= 0 &&
		!isMutating;

	return (
		<TableRow
			sx={{
				backgroundColor: backgroundColor,
				"& td": {
					verticalAlign: "middle",
					py: 0.75,
				},
			}}
		>
			<TableCell sx={dimSx}>{idx}</TableCell>

			<TableCell>
				<SectionSelector
					value={draft.SectionId}
					onChange={(value) => onChange({ ...draft, SectionId: value! })}
					size="small"
					sortBy="recent"
					hideTph
					assetId={MILL_1.id}
				/>
			</TableCell>

			<TableCell>
				<HeatSelector
					value={draft.HeatId}
					onChange={(value, heat) =>
						onChange({
							...draft,
							HeatId: value ?? null,
							GradeId: heat?.GradeId!,
						})
					}
					sectionId={draft.SectionId}
					assetId={MILL_1.id}
				/>
			</TableCell>

			<TableCell>
				<GradeSelector value={draft.GradeId} disabled assetId={MILL_1.id} />
			</TableCell>

			<TableCell>
				<FeetInchesInput
					value={draft.LengthInches}
					onChange={(v) => onChange({ ...draft, LengthInches: v ?? 0 })}
				/>
			</TableCell>

			<TableCell sx={numCellSx}>
				<TextField
					size="small"
					type="number"
					margin="none"
					fullWidth
					value={draft.GoodPieces ?? ""}
					onChange={(e) =>
						onChange({
							...draft,
							GoodPieces:
								e.target.value === "" ? 0 : Number.parseInt(e.target.value, 10),
						})
					}
					onKeyDown={blockNonIntegerKeys}
					onFocus={(e) => e.target.select()}
					inputProps={{ min: 0, step: 1, style: { textAlign: "right" } }}
					sx={{ backgroundColor: "white" }}
				/>
			</TableCell>

			<TableCell sx={numCellSx}>
				<TextField
					size="small"
					type="number"
					margin="none"
					fullWidth
					value={draft.TotalWeight ?? ""}
					onChange={(e) =>
						onChange({
							...draft,
							TotalWeight:
								e.target.value === "" ? null : Number(e.target.value),
						})
					}
					onFocus={(e) => e.target.select()}
					inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }}
					sx={{ backgroundColor: "white" }}
				/>
			</TableCell>

			<TableCell sx={numCellSx}>
				<TextField
					size="small"
					type="number"
					margin="none"
					fullWidth
					value={draft.ScrapPieces ?? ""}
					onChange={(e) =>
						onChange({
							...draft,
							ScrapPieces:
								e.target.value === ""
									? null
									: Number.parseInt(e.target.value, 10),
						})
					}
					onKeyDown={blockNonIntegerKeys}
					onFocus={(e) => e.target.select()}
					inputProps={{ min: 0, step: 1, style: { textAlign: "right" } }}
					sx={{ backgroundColor: "white" }}
				/>
			</TableCell>

			<TableCell>
				<TextField
					size="small"
					margin="none"
					fullWidth
					value={draft.ScrapReason ?? ""}
					onChange={(e) =>
						onChange({
							...draft,
							ScrapReason: e.target.value === "" ? null : e.target.value,
						})
					}
					onFocus={(e) => e.target.select()}
					sx={{ backgroundColor: "white" }}
				/>
			</TableCell>

			<TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
				<Stack direction="row" spacing={0.5} justifyContent="flex-end">
					<Tooltip title={isDirty ? "Save changes" : "No changes to save"}>
						<Box component="span">
							<IconButton
								size="small"
								color="success"
								onClick={onSave}
								disabled={!canSave}
							>
								<CheckCircleOutline fontSize="small" />
							</IconButton>
						</Box>
					</Tooltip>
					<Tooltip title="Cancel">
						<IconButton size="small" onClick={onCancel} disabled={isMutating}>
							<Close fontSize="small" />
						</IconButton>
					</Tooltip>
				</Stack>
			</TableCell>
		</TableRow>
	);
};
